import { RECENT_PROJECT_PATHS_KEY, RECENT_WORKSPACE_SLUGS_KEY } from "./local-storage.consts";
import type { Container } from "@shared/types/supabase.types";

const DEFAULT_RECENT_LIMIT = 10;

function readRecentValues(storageKey: string) {
  const rawValue = localStorage.getItem(storageKey);

  if (!rawValue) return [];

  try {
    const parsedValue = JSON.parse(rawValue);

    if (!Array.isArray(parsedValue)) return [];

    return parsedValue.filter(
      (value): value is string => typeof value === "string" && value.length > 0,
    );
  } catch {
    return [];
  }
}

function writeRecentValues(storageKey: string, values: string[]) {
  localStorage.setItem(storageKey, JSON.stringify(values));
}

function recordRecentValue(storageKey: string, value: string, limit = DEFAULT_RECENT_LIMIT) {
  const nextValues = [value, ...readRecentValues(storageKey).filter((item) => item !== value)];

  writeRecentValues(storageKey, nextValues.slice(0, limit));
}

export function recordRecentWorkspaceSlug(slug: string) {
  recordRecentValue(RECENT_WORKSPACE_SLUGS_KEY, slug);
}

export function getRecentWorkspaceSlugs() {
  return readRecentValues(RECENT_WORKSPACE_SLUGS_KEY);
}

export function getRecentProjectPath(workspaceSlug: string, projectSlug: string) {
  return `${workspaceSlug}/${projectSlug}`;
}

export function recordRecentProjectPath(workspaceSlug: string, projectSlug: string) {
  recordRecentValue(RECENT_PROJECT_PATHS_KEY, getRecentProjectPath(workspaceSlug, projectSlug));
}

export function getRecentProjectPaths() {
  return readRecentValues(RECENT_PROJECT_PATHS_KEY);
}

export function sortContainersByRecentValues<T extends Container>(
  items: T[],
  recentValues: string[],
  getValue: (item: T) => string | undefined | null,
) {
  if (recentValues.length === 0) return items;

  const recentIndex = new Map(recentValues.map((value, index) => [value, index]));

  return [...items].sort((leftItem, rightItem) => {
    const leftRecentIndex = recentIndex.get(getValue(leftItem) ?? "");
    const rightRecentIndex = recentIndex.get(getValue(rightItem) ?? "");

    if (leftRecentIndex == null && rightRecentIndex == null) return 0;
    if (leftRecentIndex == null) return 1;
    if (rightRecentIndex == null) return -1;

    return leftRecentIndex - rightRecentIndex;
  });
}
